import Alpine from 'alpinejs'
import { fragment } from '@js/util/helpers.js'

// Define (mobile) menu open/close state
Alpine.store('menu', false)
Alpine.store('desktopActiveMegaMenu', false)
Alpine.store('mobileActiveMegaMenu', false)

// Use @media query for hovering devices
let canHover = window.matchMedia('(hover: hover) and (pointer: fine)')
Alpine.store('canHover', () => canHover.matches)

// Define transition options for menu animations
Alpine.store('menuTransitions', {
  // The whole mobile menu when toggled
  mobileMenuContainer: {
    css: {
      // opacity: 0,
      transform: 'translateY(-100%) translateY(-35px)',
    },
    duration: 500,

    // Visuals controlled by the global 'menu' state
    bind: 'menu',
  },

  // A mega menu appearing/disappearing on mobile
  mobileMegaMenu: {
    css: {
      transform: 'translateX(100%) translateX(35px)',
    },
    duration: 400,
  },

  // A mega menu appearing/disappearing on desktop
  desktopMegaMenu: {
    css: {
      transform: 'translateY(-100%) translateY(calc(-1 * var(--header-height))) translateY(-35px)',
    },
    duration: 300,
  },

  // Search appearing on desktop
  desktopSearch: {
    css: {
      opacity: 0,
      transform: 'translateY(-20px)',
    },
    duration: 200,

    // Avoid pointer interactivity to avoid accidental closing/re-opening during transition
    onBeforeAnimate(element) {
      element.style.pointerEvents = 'none'
    },
    onComplete(element) {
      element.style.removeProperty('pointer-events')
    },
  },
})

let svgIcon =
  document.querySelector('#mega-menu-icon-enter')?.content.firstElementChild ??
  fragment`<svg aria-hidden="true" focusable="false" width="6" height="11" viewBox="0 0 6 11" xmlns="http://www.w3.org/2000/svg"><path d="M6 5.5c0 .435-.172.852-.481 1.16L1.163 11 0 9.84 4.355 5.5 0 1.16 1.163 0l4.355 4.34c.309.308.483.725.482 1.16Z" fill="currentColor" fill-rule="nonzero"/></svg>`

// We'll attach the Alpine menu logic via JS because it would be *very* tedious
// to do so via the various hooks WordPress provides for menu HTML generation.
document.querySelectorAll('nav.main').forEach(menuNavContainer => {
  let trigger =
    menuNavContainer.closest('[data-mega-menu-trigger]')?.dataset.megaMenuTrigger ?? 'click'

  let isMobile = menuNavContainer.matches('.header-mobile-wrapper *')

  menuNavContainer.querySelectorAll('.menu-item a').forEach(menuItemLink => {
    if (isMobile) {
      const href = menuItemLink.getAttribute('href')
      if (href.includes('#') && !href.endsWith('#')) {
        const hrefUrl = new URL(href, window.location.origin)
        const currentUrl = new URL(window.location.href)

        if (hrefUrl.pathname === currentUrl.pathname && hrefUrl.search === currentUrl.search) {
          menuItemLink.setAttribute('x-on:click', '$store.menu = false')
        }
      }
    }
  })

  // Iterate all list items that have submenus
  menuNavContainer.querySelectorAll('.mega-menu-trigger').forEach(menuItemLink => {
    let menuItem = menuItemLink.parentNode
    menuItem.classList.add('with-mega-menu')

    let megaMenu = menuItemLink
      .closest('.header-wrapper')
      .querySelector(`.mega-menu[data-mega-menu-id="${menuItemLink.dataset.megaMenuId}"]`)
    let megaMenuId = menuItemLink.dataset.megaMenuId
    let transitionId = `megaMenu_${megaMenuId}`

    // Make submenu focusable as a whole and hide it
    megaMenu.setAttribute('tabindex', '-1')

    if (isMobile) {
      /*******************************
       * Mobile menu specific functionality
       ******************************/

      // Hide mega menu unless it's the current menu item
      if (!menuItem.classList.contains('current-menu-parent')) {
        megaMenu.style.display = 'none'
      }

      // Add unique ID to mega menu for ARIA markup
      let megaMenuIdAttribute = `mega-menu-${megaMenuId}`

      megaMenu.setAttribute('id', megaMenuIdAttribute)

      // Close mega menu on anchor clicks
      megaMenu.addEventListener('click', event => {
        const link = event.target.closest('a')
        if (!link) return

        if (!link.href.includes('#')) return

        const linkUrl = new URL(link.href, window.location.origin)
        linkUrl.hash = ''
        const currentUrl = new URL(window.location.href)
        currentUrl.hash = ''

        if (linkUrl.href !== currentUrl.href) return

        Alpine.store('menu', false)
      })

      // Disable link clickability if it points to empty anchor
      // Make it show the mega menu instead

      if (menuItemLink.getAttribute('href') === '#' || trigger === 'click') {
        menuItemLink.setAttribute('x-on:click.prevent', `${transitionId}.show()`)
      }

      // Insert dropdown icon after link
      menuItemLink.after(fragment`
      <button type="button" class="dropdown" @click="${transitionId}.toggle()" :aria-expanded="${transitionId}.isVisible()" :aria-controls="${JSON.stringify(
        megaMenuIdAttribute,
      )}">
        ${svgIcon.cloneNode(true)}
      </button>
    `)
    } else {
      /*******************************
       * Desktop menu specific functionality
       ******************************/

      menuItem.setAttribute(
        ':data-with-active-mega-menu',
        `$store.desktopActiveMegaMenu === ${JSON.stringify(megaMenuId)}`,
      )

      let megaMenuSelector = `.header-desktop-wrapper .mega-menu[data-mega-menu-id="${menuItemLink.dataset.megaMenuId}"]`

      megaMenu.style.display = 'none'

      // Close mega menu on anchor clicks
      megaMenu.addEventListener('click', event => {
        const link = event.target.closest('a')
        if (!link) return

        if (!link.href.includes('#')) return

        const linkUrl = new URL(link.href, window.location.origin)
        linkUrl.hash = ''
        const currentUrl = new URL(window.location.href)
        currentUrl.hash = ''

        if (linkUrl.href !== currentUrl.href) return

        Alpine.store('desktopActiveMegaMenu', false)
      })

      // Toggle menu on mouseenter/mouseleave for mouse navigation
      megaMenu.setAttribute(':style', '{ zIndex: latestEl === $el ? zIndex : null }')
      menuItem.setAttribute('x-data', '{ megaMenu: null }')
      menuItem.setAttribute(
        'x-init',
        `megaMenu = document.querySelector(${JSON.stringify(megaMenuSelector)})`,
      )

      switch (trigger) {
        case 'hover': {
          // Insert focusable element before link
          menuItemLink.before(
            fragment`<div x-show="$data.${transitionId}?.isVisible()" class="sr-only" tabindex="0" @focus="$store.desktopActiveMegaMenu = false"></div>`,
          )

          // Insert dropdown button after link
          menuItemLink.after(
            fragment`
              <div x-show="$data.${transitionId}?.isVisible()" class="sr-only" tabindex="0" @focus="megaMenu.focus()"></div>
              <button type="button" class="dropdown" @click="${transitionId}.show()" tabindex="-1">
                <svg width="12" height="10" viewBox="0 0 12 10" xmlns="http://www.w3.org/2000/svg"><path d="M12 4.996a1.646 1.646 0 0 0-.506-1.168L7.817.243a.874.874 0 0 0-1.208 0 .823.823 0 0 0-.254.592c0 .222.091.435.254.592l2.82 2.735H.857A.846.846 0 0 0 0 4.996c0 .46.384.834.857.834H9.43l-2.82 2.744a.819.819 0 0 0-.005 1.18.875.875 0 0 0 1.213.004l3.677-3.586c.322-.312.504-.734.506-1.176Z" fill="#000" fill-rule="nonzero"/></svg>
              </button>
            `,
          )

          menuItem.setAttribute(
            'x-on:mouseenter',
            `
            zIndex++
            latestEl = megaMenu
            if ($store.canHover()) {
              ${transitionId}.show()
            }
            `,
          )

          // Toggle menu on focusin/focusout for keyboard navigation
          menuItem.setAttribute(
            'x-on:focusin',
            `
            (
              // Dropdown click
              $event.target.matches('button, button *') ||

              // Keyboard focus
              document.documentElement.classList.contains('no-focus-visible') ||
              $el.querySelector('a').matches(':focus-visible')
            ) &&

            ${transitionId}.show()
            `,
          )
          megaMenu.setAttribute(
            'x-on:focusout',
            `setTimeout(() => {
              // Timeout is needed because in the moment the event occurs, there is no focused element
              !${transitionId}.isAnimating($el) && !$el.querySelector(':focus') && ${transitionId}.hide()
            }, 0)`,
          )
          break
        }

        case 'click': {
          menuItemLink.setAttribute(
            'x-on:click.prevent',
            `
            ${transitionId}.toggle()
            let isVisible = ${transitionId}.isVisible()

            if (isVisible) {
              setTimeout(() => {
                megaMenu.focus()
              }, 0)
            }
            `,
          )
          break
        }

        default:
          throw new Error(`Unknown trigger type: ${trigger}`)
      }
    }

    menuItem.setAttribute(
      ':class',
      `$data.${transitionId}?.isVisible() ? 'rotate-dropdown-arrow with-mega-menu-expanded' : null`,
    )
  })
})
